import { Logger, LoggerService } from "@Shared/logger";
import { CookieManager } from "@Shared/storage/cookie_manager";
import { DomainUtil } from "@Shared/utils";
import {
  BLUECONIC_LOWA_COOKIE_EXPIRES_SECONDS,
  BLUECONIC_LOWA_COOKIE_NAME,
  BLUECONIC_LOWA_SEGMENTS,
  BLUECONIC_LOAD_VALUES_TIMEOUT_MS,
  BLUECONIC_PROFILE_READY_TIMEOUT_MS,
} from "../_models/blueconic.constants";
import { BcLowaCheckResult, BlueConicProfileInstance } from "../_models/blueconic.types";
import { BlueConicReadinessService } from "./blueconic-readiness.service";

export class BlueConicLowaService {
  private static instance: BlueConicLowaService;

  static get Instance(): BlueConicLowaService {
    if ( this.instance === null || this.instance === undefined ) {
      this.instance = new BlueConicLowaService();
    }
    return this.instance;
  }

  private Logger: Logger = LoggerService.createLogger("BlueConicLowaService");

  constructor() {
    this.Logger.trace("****  Blueconic LOWA CTOR ****");
  }

  public async bcLowaCheck( setBCLowaCookie = false ): Promise<BcLowaCheckResult> {
    let isSub = false;
    let uuid: string | undefined;
    const loadStatus = await BlueConicReadinessService.Instance.blueConicReady();
    this.Logger.trace("bcLowaCheck | BC load status: ", loadStatus);

    if ( loadStatus && window.blueConicClient?.getSegments ) {
      const bcSegments = window.blueConicClient.getSegments();
      this.Logger.trace("bcLowaCheck | BC segments for users: ", bcSegments);
      const userSubSegments = bcSegments.filter(
        ( segment ) => ( BLUECONIC_LOWA_SEGMENTS as readonly string[] ).includes( segment.name )
      );
      this.Logger.trace("bcLowaCheck | BC userSubSegments: ", userSubSegments);

      if ( userSubSegments.length > 0 ) {
        const bcUuid = await this.bcGetAuth0Id();
        if ( bcUuid ) {
          isSub = true;
          uuid = bcUuid;
        }
      }
    }

    if ( setBCLowaCookie && isSub ) {
      this.addBCLowaCookie();
    }

    const returnObj: BcLowaCheckResult = { isSub };
    if ( uuid ) returnObj.uuid = uuid;
    return returnObj;
  }

  public async bcGetAuth0Id(): Promise<string | false> {
    const loadStatus = await BlueConicReadinessService.Instance.blueConicReady();
    this.Logger.trace("bcGetAuth0Id | BC loaded:", loadStatus);

    if ( !loadStatus || !window.blueConicClient?.profile ) {
      return false;
    }

    const bcLoadSuccess = await this.blueConicProfileReady();
    this.Logger.trace("bcGetAuth0Id | profile ready:", bcLoadSuccess);

    if ( !bcLoadSuccess ) return false;

    const profile = window.blueConicClient.profile.getProfile();
    const valuesLoaded = await this.loadProfileValues( profile, ["auth0_id"] );
    if ( !valuesLoaded ) {
      this.Logger.trace("bcGetAuth0Id | loadValues timed out or failed");
      return false;
    }

    const auth0Id = profile.getValue( "auth0_id" );
    this.Logger.trace("bcGetAuth0Id | auth0_id:", auth0Id);
    return auth0Id || false;
  }

  private loadProfileValues(
    profile: BlueConicProfileInstance,
    keys: string[],
    timeoutMs = BLUECONIC_LOAD_VALUES_TIMEOUT_MS
  ): Promise<boolean> {
    return new Promise<boolean>(( resolve ) => {
      let settled = false;
      const finish = ( success: boolean ): void => {
        if ( settled ) return;
        settled = true;
        resolve( success );
      };

      const timeoutId = setTimeout( () => {
        this.Logger.trace( "loadProfileValues | timeout after", timeoutMs, "ms for keys:", keys );
        finish( false );
      }, timeoutMs );

      profile.loadValues( keys, null, () => {
        clearTimeout( timeoutId );
        finish( true );
      } );
    } );
  }

  private async blueConicProfileReady(): Promise<boolean> {
    this.Logger.trace("blueConicProfileReady | Waiting for BC Profile to be ready.");

    const client = window.blueConicClient;
    if ( client && ( client.getSegments || client.profile ) ) {
      this.Logger.trace("blueConicProfileReady | BC getSegments is ready.");
      return true;
    }

    if ( !client?.event ) {
      return false;
    }

    return new Promise<boolean>(( resolve ) => {
      const unsubscribe = client.event!.subscribe(
        client.event!.onBeforeInteractions,
        {},
        () => {
          this.Logger.trace(
            "blueConicProfileReady | onBeforeInteractions fired, BC profile is ready."
          );
          unsubscribe?.();
          resolve( true );
        }
      );

      setTimeout( () => {
        unsubscribe?.();
        this.Logger.trace(
          "blueConicProfileReady | onBeforeInteractions timeout, BC profile readiness unknown"
        );
        resolve( false );
      }, BLUECONIC_PROFILE_READY_TIMEOUT_MS );
    } );
  }

  private addBCLowaCookie(): void {
    CookieManager.setCookie( BLUECONIC_LOWA_COOKIE_NAME, "1", {
      path: "/",
      domain: this.getCookieRootDomain(),
      expires: BLUECONIC_LOWA_COOKIE_EXPIRES_SECONDS,
      secure: true,
    } );
  }

  private getCookieRootDomain(): string {
    const hostSegments = window.location.hostname.split( "." );
    if ( hostSegments.length < 2 ) {
      return window.location.hostname;
    }
    return `.${DomainUtil.getRootDomain()}`;
  }
}
